import { Tooltip } from 'radix-ui';
import React from 'react';
import ReactCountryFlag from 'react-country-flag';

import { useTranslation } from '~/app';

interface CountryFlagTooltipProps {
  countryCode: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

// Get country name using browser's Intl API
const getCountryName = (countryCode: string, locale: string): string => {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    return displayNames.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
};

const CountryFlagTooltip: React.FC<CountryFlagTooltipProps> = ({
  countryCode,
  className = '',
  size = 'md',
  showTooltip = true,
}) => {
  const { locale } = useTranslation();
  const sizeClasses = {
    sm: 'w-auto h-2',
    md: 'w-auto h-3',
    lg: 'w-auto h-5',
  };

  const countryName = getCountryName(countryCode, locale);

  if (!showTooltip) {
    return (
      <ReactCountryFlag
        className={`${sizeClasses[size]} ${className}`}
        title={countryName}
        aria-label={countryName}
        countryCode={countryCode}
        svg
      />
    );
  }

  return (
    <Tooltip.TooltipProvider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <div className="inline-block">
            <ReactCountryFlag
              className={`${sizeClasses[size]} ${className}`}
              title={countryName}
              aria-label={countryName}
              countryCode={countryCode}
              svg
            />
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={8}
            className="z-50 rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-xl"
          >
            {countryName}
            <Tooltip.Arrow className="fill-white" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.TooltipProvider>
  );
};

export default CountryFlagTooltip;
